import type { Transforms } from "diagnostics-domain/dashboard/type";
import { groupBy } from "diagnostics-lib/iterable";
const aggregations = {
  noop() {
    return null;
  },
  sum<T extends { [key: string]: boolean | number | string | null | undefined }[]>(
    dataset: T,
    column: string,
  ) {
    let r = 0;
    for (const row of dataset) {
      /**
       * @note type coercion isNaN
       * @see {https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isNaN}
       */
      if (
        row[column] === null ||
        row[column] === undefined ||
        typeof row[column] === "boolean" ||
        window.isNaN(row[column] as number)
      )
        continue;
      r += Number(row[column]);
    }
    return r;
  },
  /** @note developed for testing purpose, but not used. Kept for future needs */
  // min<T extends { [key: string]: boolean | number | string | null | undefined }[]>(
  //   dataset: T,
  //   column: string,
  // ) {
  //   return dataset.reduce((acc, row) => {
  //     if (!row[column] || Number.isNaN(Number(row[column]))) return acc;
  //     return Math.min(acc, Number(row[column]));
  //   }, Infinity);
  // },
  // max<T extends { [key: string]: boolean | number | string | null | undefined }[]>(
  //   dataset: T,
  //   column: string,
  // ) {
  //   return dataset.reduce((acc, row) => {
  //     if (!row[column] || Number.isNaN(Number(row[column]))) return acc;
  //     return Math.max(acc, Number(row[column]));
  //   }, -Infinity);
  // },
  // mean<T extends { [key: string]: boolean | number | string | null | undefined }[]>(
  //   dataset: T,
  //   column: string,
  // ) {
  //   const l = dataset.filter(row => row[column] !== undefined && row[column] !== null).length;
  //   return aggregations.sum(dataset, column) / l;
  // },
  // median<T extends { [key: string]: boolean | number | string | null | undefined }[]>(
  //   dataset: T,
  //   column: string,
  // ) {
  //   const sorted = dataset
  //     .map(row => row[column])
  //     .filter(v => v !== undefined && v !== null)
  //     .sort((a, b) => (Number(a) > Number(b) ? 1 : -1));
  //   const middle = Math.floor(sorted.length / 2);
  //   if (sorted.length % 2 === 0) {
  //     return (Number(sorted[middle - 1]) + Number(sorted[middle])) / 2;
  //   }
  //   return Number(sorted[middle]);
  // },
};

const filters = {
  oneOf<T>(value: T, predicate: Set<T>) {
    return predicate.has(value);
  },
};

export function applyAggregate<
  T extends { [key: string]: boolean | number | string | null | undefined }[],
>(dataset: T, aggregate: NonNullable<Transforms[number]["aggregate"]>[number]) {
  if (!dataset[0]) return dataset;
  /* const uniqueKeys = Array.from(new Set(dataset.flatMap(d => Object.keys(d)))); */
  /** @note Dataset data model expect all entries to have the sames keys before transforms */
  const uniqKeys = Object.keys(dataset[0]);

  const columns = uniqKeys
    .filter(key =>
      aggregate.column === "quantityImpacts"
        ? key.startsWith("quantityImpacts/")
        : key === aggregate.column,
    )
    .map(key => ({ in: key, out: key.replace(aggregate.column, aggregate.as) }));

  const op = aggregations[aggregate.operation as keyof typeof aggregations];
  if (!op) return dataset;

  const tmp: Record<string, ReturnType<typeof op>> = {};
  for (const column of columns) {
    tmp[column.out] = op(dataset, column.in);
  }

  const out = [];
  for (const row of dataset) {
    for (const column of columns) {
      row[column.out] = tmp[column.out];
    }
    out.push(row);
  }
  return out;
}

export function applyFilter<
  T extends { [key: string]: boolean | number | string | null | undefined }[],
>(dataset: T, filter: NonNullable<Transforms[number]["filter"]>[number]) {
  switch (filter.operation) {
    case "oneOf": {
      if (!Array.isArray(filter.value)) return dataset;
      const predicates = new Set(filter.value);
      return dataset.filter(data => filters.oneOf(data[filter.column], predicates));
    }
    default:
      return dataset;
  }
}

export function applyTransforms(
  dataset: { [key: string]: boolean | number | string | null | undefined }[],
  transforms: Transforms,
) {
  const columns = Object.keys(dataset[0] ?? {});

  let out = dataset;

  for (const t of transforms) {
    const hasAggregates = t.aggregate && t.aggregate.length > 0;
    const hasFilters = t.filter && t.filter.length > 0;
    const hasGroupBy = t.groupBy && t.groupBy.length > 0;
    if (!hasAggregates && !hasFilters && !hasGroupBy) continue;

    const result = groupBy(
      out,
      row =>
        t.groupBy
          ?.filter(g => g !== "eventName")
          .map(key => row[key])
          .join("-") ?? row["rowId"],
    );

    const groupValues = Array.from(result.values());
    const transformResults = [];
    for (const rows of groupValues) {
      let o = rows;
      if (hasFilters && !hasAggregates) {
        const filters = t.filter?.filter(f => columns.includes(f.column)) ?? [];
        o = filters.reduce((acc, f) => applyFilter(acc, f), rows);
      }

      if (hasAggregates && !hasFilters) {
        o = t.aggregate?.reduce((acc, agg) => applyAggregate(acc, agg), rows) ?? rows;
      }

      if (hasAggregates && hasFilters) {
        const filteredRows = t.filter?.reduce((acc, f) => applyFilter(acc, f), rows) ?? [];
        const aggregation =
          t.aggregate?.reduce((acc, agg) => applyAggregate(acc, agg), filteredRows) ?? [];
        const ref = aggregation[0];

        const refKeys = ref ? Object.keys(ref) : null;
        o = rows.map(row => {
          if (!ref || !refKeys || !t.aggregate) return row;
          for (const { as } of t.aggregate) {
            const keys = refKeys.filter(key => key === as || key.includes(as));
            for (const key of keys) {
              if (Object.hasOwn(row, key)) continue;
              row[key] = ref[key];
            }
          }
          return row;
        });
      }
      transformResults.push(...o);
    }
    out = transformResults;

    // out = Array.from(
    //   result
    //     .mapValues(rows => {
    //     })
    //     .values(),
    // ).flat(1);
  }
  return out;
}
